import { DataTable } from "@/components/custom/data-table";
import { Box, styled, Typography, Button, Divider } from "@mui/material";
import { TransactionPerformanceColumns } from "./transaction-performance-column";
import { useState } from "react";
import { useChannelStats } from "@/hooks/use-channel-stats";
import { formatCurrency } from "@/utils/format-currency";
import { formatLabel } from "@/utils/format-label";
import { CustomButton } from "@/components/custom/custom-button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const PeriodButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  minWidth: 48,
  height: 32,
  borderRadius: 20,
  fontSize: "0.8rem",
  textTransform: "none",
  fontWeight: isActive ? 600 : 400,
  backgroundColor: isActive ? theme.palette.primary.main : "transparent",
  color: isActive ? "#fff" : theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
}));

const PeriodSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : theme.palette.grey[100],
  borderRadius: 20,
  padding: 4,
  width: "fit-content",
  gap: 2,
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "12px 20px",
  borderRadius: "8px",
}));

const periods = ["7d", "14d", "30d", "60d", "90d"];

const toRow = (channel) => ({
  id: channel.channel,
  name: formatLabel(channel.channel),
  transaction_count: channel.totalCount,
  transaction_count_formatted: channel.totalCount?.toLocaleString(),
  transaction_volume: channel.totalVolume,
  transaction_volume_formatted: formatCurrency(channel.totalVolume),
  success_count: channel.successCount,
  success_count_formatted: channel.successCount?.toLocaleString(),
  failed_count: channel.failedCount,
  failed_count_formatted: channel.failedCount?.toLocaleString(),
  success_rate: channel.successRate,
  avg_transaction_value: channel.averageValue,
  avg_transaction_value_formatted: formatCurrency(channel.averageValue),
});

const TransactionPerformance = () => {
  const [period, setPeriod] = useState("30d");
  const { data, isLoading, isError } = useChannelStats(period);
  const rows = (data?.channels || []).map(toRow);

  return (
    <Card>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Transaction performance
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Transaction performance by channel
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PeriodSelector>
            {periods.map((p) => (
              <PeriodButton
                key={p}
                isActive={period === p}
                onClick={() => setPeriod(p)}
              >
                {p}
              </PeriodButton>
            ))}
          </PeriodSelector>

          <Divider orientation="vertical" flexItem />

          <CustomButton
            variantType="outline"
            endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={() => console.log("export")}
            sx={{ height: 35 }}
          >
            Export
          </CustomButton>
        </Box>
      </Box>
      <DataTable
        data={rows}
        columns={TransactionPerformanceColumns}
        isLoading={isLoading}
        isError={isError}
      />
    </Card>
  );
};
export default TransactionPerformance;
